/* eslint-disable react/no-unescaped-entities */
import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import ArticleCard from "@/components/articles/ArticleCard";
import { submitLead } from "@/lib/api";
import { fetchArticles, fetchSiteContent } from "@/lib/api";
import { getLeadSource } from "@/lib/leadSource";
import { setPageMeta } from "@/lib/pageMeta";

const assetUrl = (u, w) => {
  if (!u) return "";
  const full = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (!w || !/\/api\/files\//.test(full)) return full;
  return `${full}${full.includes("?") ? "&" : "?"}w=${w}`;
};

const WHATSAPP_URL = "https://wa.me/917208286283";

export default function HomePage() {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({ open: false, type: "meeting" });
  const [siteContent, setSiteContent] = useState({});
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  useEffect(() => {
    let alive = true;
    fetchSiteContent().then((c) => { if (alive) setSiteContent(c || {}); }).catch(() => {});
    setPageMeta({
      title: "Utsah — Thoughtful Corporate Gifting",
      description: "Thoughtfully curated corporate gifts designed to celebrate employees, clients and partners.",
      canonicalPath: "/",
    });
    return () => { alive = false; };
  }, []);

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />
      <HeroCarousel navigate={navigate} siteContent={siteContent} />
      <TrustedCompanies />
      <DiwaliShowcase navigate={navigate} siteContent={siteContent} />
      <TreePlanting navigate={navigate} siteContent={siteContent} />
      <WeComeToYou onBookMeeting={openMeeting} />
      <PrivilegeProgram onExplore={() => navigate("/partner-benefits")} siteContent={siteContent} />
      <HowItWorksHome siteContent={siteContent} />
      <FAQHome />
      <ContactSection />
      <InsightsSection navigate={navigate} />
      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source="Home Page" onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" toastOptions={{ style: { fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 } }} />
    </div>
  );
}

/* ---------- HERO ---------- */
/* Two CMS-driven banner images (desktop + mobile). Heading, subheading and CTA
   are defined here in code and rendered as an overlay directly on top of the
   image, using the existing Utsah editorial typography (`.section-headline`
   pattern: DM Serif Display with italic orange accent inside <em>). */
const SLIDES = [
  {
    imageKey: "home_hero_1",
    mobileImageKey: "home_hero_1_mobile_image",
    heading: (<>Gifts that <em>Celebrate People.</em></>),
    subheading: "Thoughtfully curated corporate gifting for employees, clients and partners—designed to make every gesture more meaningful.",
    ctaLabel: "Explore Collections",
    ctaLink: "/diwali-collection",
    alt: "Utsah — Gifts that Celebrate People",
  },
  {
    imageKey: "home_hero_2",
    mobileImageKey: "home_hero_2_mobile_image",
    heading: (<>A Gift for Your Team.<br /><em>A Tree for Our Planet.</em></>),
    subheading: "With every eligible hamper, a tree is planted in your company's name through our trusted reforestation partner—creating an impact that grows long after the gift has been opened.",
    ctaLabel: "How It Works",
    ctaLink: "/give-back",
    alt: "Utsah — A Gift for Your Team, A Tree for Our Planet",
  },
];

function HeroCarousel({ navigate, siteContent }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  const onCTAClick = (e, link) => {
    if (!link) return;
    if (link.startsWith("#")) {
      e.preventDefault();
      const el = document.getElementById(link.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (link.startsWith("/")) {
      e.preventDefault();
      navigate(link);
    }
  };

  return (
    <section className="home-hero-full" data-testid="home-hero">
      {SLIDES.map((slide, idx) => {
        const desktop = siteContent?.[slide.imageKey] || "";
        const mobile = siteContent?.[slide.mobileImageKey] || "";
        const isExternal = slide.ctaLink && !slide.ctaLink.startsWith("/") && !slide.ctaLink.startsWith("#");
        const hasAnyImage = Boolean(desktop || mobile);
        return (
          <div
            key={slide.imageKey}
            className={`home-hero-full-slide ${i === idx ? "active" : ""}`.trim()}
            aria-hidden={i !== idx}
          >
            <div className="home-hero-full-media">
              {hasAnyImage ? (
                <picture className="home-hero-full-picture">
                  {mobile ? <source media="(max-width: 720px)" srcSet={assetUrl(mobile, 900)} /> : null}
                  <img
                    src={assetUrl(desktop || mobile, 1920)}
                    alt={slide.alt}
                    data-testid={`home-hero-img-${idx}`}
                    fetchpriority={i === 0 ? "high" : "auto"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                  />
                </picture>
              ) : (
                <div className="home-hero-full-placeholder">
                  <div className="hero-placeholder-label">Upload banner in Admin → Site Content</div>
                </div>
              )}
              <div className="home-hero-full-dots">
                {SLIDES.map((_, di) => (
                  <button
                    key={di}
                    className={`home-hero-dot ${i === di ? "active" : ""}`.trim()}
                    aria-label={`Slide ${di + 1}`}
                    onClick={() => setI(di)}
                    data-testid={`home-hero-dot-${di}`}
                  />
                ))}
              </div>
              <div className="home-hero-full-overlay">
                <h1 className="home-hero-full-heading" data-testid={`home-hero-heading-${idx}`}>{slide.heading}</h1>
                <p className="home-hero-full-subheading" data-testid={`home-hero-subheading-${idx}`}>{slide.subheading}</p>
                <a
                  href={slide.ctaLink}
                  className="hero-cta home-hero-full-cta"
                  onClick={(e) => onCTAClick(e, slide.ctaLink)}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  data-testid={`home-hero-cta-${idx}`}
                >
                  {slide.ctaLabel}
                  <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </a>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* ---------- TRUSTED COMPANIES + MARQUEE ---------- */
const CLIENT_LOGOS = [
  { name: "Reliance Smart", brand: "reliance", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/n79p6xid_reliance%20smart%20logo.png" },
  { name: "Amazon", brand: "amazon", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/e2qdgf3y_image.png" },
  { name: "Indus Towers", brand: "indus", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/92h9bhm4_indus%20tower%20logo.png" },
  { name: "Fortis Healthcare", brand: "fortis", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/y9bss5wd_image.png" },
  { name: "MOC Cancer Care & Research Centre", brand: "moc", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/2gdltlre_MOC_Logo_Page2.webp" },
  { name: "University of Manchester", brand: "manchester", src: "https://customer-assets-wrfwihn1.emergentagent.net/job_corporate-identity-10/artifacts/slojxhg2_Manchester%20Met_RGB_Black_Landscape.webp" },
];
function TrustedCompanies() {
  const doubled = [...CLIENT_LOGOS, ...CLIENT_LOGOS];
  return (
    <>
      <section className="section home-trusted" data-testid="home-trusted-section">
        <div className="section-inner">
          <Reveal as="h2" className="home-trusted-headline">Trusted by Leading Companies Across India</Reveal>
          <Reveal as="p" delay={1} className="home-trusted-body">
            From fast-growing businesses to leading enterprises, we've helped organisations celebrate employees, clients and partners through thoughtful corporate gifting experiences.
          </Reveal>
          <div className="home-stats-grid">
            <StatCard num="30,000+" label="Hampers Delivered" />
            <StatCard num="150+" label="Happy Clients" />
            <StatCard numMulti={<>Reliance<br />Smart Bazaar</>} label="Gift Partner" />
            <StatCard num="99.97%" label="On Time Delivery" />
          </div>
        </div>
      </section>
      <div className="marquee marquee-cream" data-testid="home-client-marquee">
        <div className="marquee-track">
          {doubled.map((logo, i) => (
            <div key={`${logo.name}-${i}`} className="marquee-item" data-testid={`client-logo-${i}`}>
              <img src={logo.src} alt={logo.name} data-brand={logo.brand} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
function StatCard({ num, numMulti, label }) {
  return (
    <div className="home-stat-card">
      <div className={`home-stat-num ${numMulti ? "multi" : ""}`.trim()}>{numMulti || num}</div>
      <div className="home-stat-label">{label}</div>
    </div>
  );
}

/* ---------- DIWALI SHOWCASE (CMS-driven image) ---------- */
function DiwaliShowcase({ navigate, siteContent }) {
  const desktop = siteContent?.home_diwali_showcase_image || "";
  const mobile = siteContent?.home_diwali_showcase_mobile_image || "";
  const hasImage = Boolean(desktop || mobile);
  return (
    <section className="section home-diwali" id="collection" data-testid="home-diwali-section">
      <div className="section-inner s5-grid">
        <div className="s5-media">
          {hasImage ? (
            <picture className="s5-picture">
              {mobile ? <source media="(max-width: 720px)" srcSet={assetUrl(mobile)} /> : null}
              <img src={assetUrl(desktop || mobile)} alt="Diwali 2026 — Curated Gifts by Utsah" data-testid="diwali-showcase-image" />
            </picture>
          ) : (
            <div className="s5-video-placeholder" data-testid="diwali-showcase-placeholder">
              <div className="s5-video-label">Upload image in Admin → Site Content → Diwali 2026 Showcase</div>
            </div>
          )}
        </div>
        <div className="s5-content">
          <Reveal className="eyebrow">Diwali 2026</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            {"Curated Gifts for\u00a0a"}<br /><em>Memorable Diwali.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20 }}>
            Thoughtfully curated corporate gifts designed to celebrate employees, clients and partners with a memorable gifting experience.
            <br /><br />
            Personalise every gift with your branding, custom gift sleeves, thoughtful packaging and a personalised message to create a lasting impression.
          </Reveal>
          <Reveal delay={3}>
            <button className="hero-cta" style={{ marginTop: 32 }} onClick={() => navigate("/diwali-collection")} data-testid="home-shop-diwali-btn">
              Explore Diwali Collection
              <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- TREE PLANTING ---------- */
function TreePlanting({ navigate, siteContent }) {
  const treeImg = siteContent?.home_tree_image || "";
  return (
    <section className="section home-tree" id="story" data-testid="home-tree-section">
      <div className="section-inner s3-grid">
        <div>
          <Reveal className="eyebrow">Gift With Purpose</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Let's Plant a <em>Tree Together</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20 }}>
            At Utsah, we believe every celebration should create a lasting impact. Through our partnership with Grow-Trees, every gift plants a tree in your company's name across verified reforestation projects in India. Simply choose the project you'd like to support, and we'll take care of the rest—including a verified tree plantation certificate with every gift.
            <br /><br />
            <strong>Help us achieve our goal of planting 30,000 trees and demonstrate your company's commitment to sustainability.</strong>
          </Reveal>
          <Reveal delay={3} className="s3-counter">
            <div className="s3-label">Our Target for 2026</div>
            <div className="s3-stat-row">
              <span className="s3-num">30,000</span>
              <span className="s3-trees-label">Trees</span>
            </div>
            <button className="hero-cta" style={{ marginTop: 32 }} onClick={() => navigate("/give-back")} data-testid="home-explore-projects-btn">
              Explore Our Reforestation Projects
              <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </Reveal>
        </div>
        <Reveal
          delay={1}
          className="s3-image-placeholder"
          style={treeImg ? { backgroundImage: `url(${assetUrl(treeImg)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          data-testid="home-tree-image"
        >
          {!treeImg ? (
            <>
              <svg width="56" height="56" viewBox="0 0 64 64" fill="none" aria-hidden>
                <path d="M32 4 L38 18 L52 18 L40 26 L44 42 L32 34 L20 42 L24 26 L12 18 L26 18 Z" fill="var(--u-orange)" opacity="0.28" />
                <rect x="28" y="40" width="8" height="18" fill="var(--u-orange)" opacity="0.22" />
              </svg>
              <div className="add-image-label">Add forest / nature image here</div>
            </>
          ) : null}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- WE COME TO YOU ---------- */
function WeComeToYou({ onBookMeeting }) {
  return (
    <section className="section s6" data-testid="home-come-to-you-section">
      <div className="section-inner s6-inner">
        <Reveal className="eyebrow cream cta-eyebrow" style={{ marginLeft: "auto", marginRight: "auto" }}>Get a Gifting Consultation at Your Office</Reveal>
        <Reveal as="h2" delay={1} className="cta-headline">
          100+ options.<br /><em>One meeting. Zero hassle.</em>
        </Reveal>
        <Reveal as="p" delay={2} className="cta-body">
          No more chasing samples from multiple vendors. Book one meeting and we arrive at your office with 100+ curated gift options — ready for you to see, touch, and choose from. One visit. Perfect gift. Sorted.
        </Reveal>
        <Reveal delay={3}>
          <button className="btn-primary" onClick={onBookMeeting} data-testid="home-book-meeting-btn">Book a Meeting</button>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- UTSAH PRIVILEGE PROGRAM ---------- */
function PrivilegeProgram({ onExplore, siteContent }) {
  const desktopImg = siteContent?.home_privilege_image || "";
  const mobileImg = siteContent?.home_privilege_mobile_image || "";
  const hasImage = Boolean(desktopImg || mobileImg);
  return (
    <section className="section home-privilege" data-testid="home-privilege-section">
      <div className="section-inner privilege-two-col">
        <div className="privilege-left">
          <Reveal className="eyebrow">Exclusive Bonus</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Get access to the <em>Utsah Partner Benefits Program.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20 }}>
            Every Utsah hamper includes access to the <strong>Utsah Partner Benefits Program</strong>, featuring exclusive offers from leading shopping, lifestyle and entertainment brands. It's our way of extending the joy of gifting beyond the moment the box is opened.
          </Reveal>
          <Reveal delay={3}>
            <button className="hero-cta" style={{ marginTop: 32 }} onClick={onExplore} data-testid="home-privilege-cta">
              Explore Utsah Partner Benefits
              <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </Reveal>
        </div>
        <div className="privilege-right">
          <div className="privilege-image-wrap" data-testid="privilege-image">
            {hasImage ? (
              <picture className="privilege-image-picture">
                {mobileImg ? <source media="(max-width: 720px)" srcSet={assetUrl(mobileImg)} /> : null}
                <img src={assetUrl(desktopImg || mobileImg)} alt="Utsah Partner Benefits — featured brands" />
              </picture>
            ) : (
              <div className="privilege-image-placeholder">
                <div className="add-image-label">Upload composite in Admin → Site Content → Exclusive Bonus</div>
              </div>
            )}
          </div>
          <div className="privilege-disclaimer">
            Representative partner brands. Available partner offers are subject to change.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HOW IT WORKS ---------- */
const HOME_STEPS = [
  { num: "Step 01", title: "Book a Meeting", desc: "Call or WhatsApp us. We understand your occasion, team size, and budget.", key: "home_step_1_image" },
  { num: "Step 02", title: "We Bring the Showroom", desc: "We visit your office with 100+ options or send samples. You choose at your convenience.", key: "home_step_2_image" },
  { num: "Step 03", title: "Dedicated Account Manager", desc: "Select your collection, add your logo, and choose your Grow-Trees project.", key: "home_step_3_image" },
  { num: "Step 04", title: "Delivered Without Stress", desc: "Dedicated account manager. Pan-India delivery. Gift box, impact certificate, and exclusive coupons — delivered.", key: "home_step_4_image" },
];
function HowItWorksHome({ siteContent }) {
  return (
    <section className="section home-steps-section" id="how-it-works" data-testid="home-steps-section">
      <div className="section-inner">
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Our Process</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            From first call to<br /><em>final delivery. Sorted.</em>
          </Reveal>
        </div>
        <div className="home-steps-grid">
          {HOME_STEPS.map((s, i) => {
            const img = siteContent?.[s.key] || "";
            return (
              <Reveal key={s.num} delay={i > 3 ? 3 : i} className="home-step-card">
                <div
                  className="home-step-img-placeholder"
                  style={img ? { backgroundImage: `url(${assetUrl(img)})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                  data-testid={`home-step-image-${i + 1}`}
                >
                  {!img ? <div className="add-image-label" style={{ fontSize: 10 }}>Add photo</div> : null}
                </div>
                <div className="home-step-num">{s.num}</div>
                <div className="home-step-title">{s.title}</div>
                <div className="home-step-desc">{s.desc}</div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
const HOME_FAQS = [
  ["What is the minimum order quantity?", "Our minimum order quantity is <strong>50 pieces</strong>. If you're looking for a smaller quantity or have a unique requirement, speak to our gifting specialist and we'll be happy to explore the best options for you."],
  ["Can I customise the gift hampers?", "Yes. You can personalise the <strong>gift box sleeve, greeting card, and selected products</strong> with your company branding. Customisation options vary depending on the products you choose."],
  ["What is the delivery timeline?", "Our standard production and delivery timeline is <strong>14 working days</strong>. Your gifting specialist will confirm the exact timeline based on your order and customisation requirements."],
  ["Do you deliver across India?", "Yes. We deliver across India, whether you're sending gifts to a single office location or multiple employee and client addresses nationwide."],
  ["What if some products arrive damaged?", "If any products arrive damaged or defective, we'll replace <strong>only the affected items</strong> promptly at no additional cost."],
  ["How do I participate in the tree planting initiative?", "Choose a collection that includes our tree planting initiative and select your preferred reforestation project. Every eligible hamper includes a <strong>verified tree plantation certificate</strong>."],
  ["How does the Utsah Partner Benefits Program work?", "Every eligible hamper includes access to the <strong>Utsah Partner Benefits Program</strong>. Once recipients receive their gift, they'll find exclusive offers from leading shopping, lifestyle and entertainment brands inside the gift box, ready to redeem."],
  ["What are your payment terms?", "Our payment terms are <strong>50% advance and 50% before dispatch</strong>. We operate on a strict no-credit policy for all orders."],
  ["Why choose Utsah over other gifting companies?", "Our hampers are thoughtfully curated and competitively priced. Eligible collections also include our tree planting initiative, and recipients receive exclusive brand offers through the <strong>Utsah Partner Benefits Program</strong>, creating a gifting experience that delivers lasting value beyond the hamper itself."],
  ["Can you help us choose the right gifting solution?", "Absolutely. Our gifting specialists will recommend products based on your budget, occasion, company culture and branding requirements to help you create the perfect gifting experience."],
];
function FAQHome() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section className="section faq" data-testid="home-faq-section">
      <div className="section-inner">
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Got Questions</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">Everything you need <em>to know.</em></Reveal>
        </div>
        <div className="home-faq-grid">
          {HOME_FAQS.map(([q, a], i) => {
            const open = openIndex === i;
            return (
              <div key={q} className={`faq-item ${open ? "open" : ""}`.trim()} data-testid={`home-faq-${i + 1}`}>
                <button className="faq-q" onClick={() => setOpenIndex(open ? null : i)} style={{ width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }} data-testid={`home-faq-toggle-${i + 1}`}>
                  <h3 className="faq-q-text">{q}</h3>
                  <div className="faq-q-icon">+</div>
                </button>
                {open && <div className="faq-a" dangerouslySetInnerHTML={{ __html: a }} />}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- CONTACT ---------- */
const INITIAL = { name: "", company: "", phone: "", email: "", budget: "", quantity: "", question: "" };
function ContactSection() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name.trim()) missing.push("name");
    if (!form.email.trim()) missing.push("email");
    if (!form.phone.trim()) missing.push("phone");
    if (!form.company.trim()) missing.push("company");
    if (missing.length) { toast.error(`Please fill in your ${missing.join(", ")}.`); return; }
    setSubmitting(true);
    try {
      const messageParts = [];
      if (form.budget.trim()) messageParts.push(`Budget: ${form.budget.trim()}`);
      if (form.question.trim()) messageParts.push(form.question.trim());
      const payload = {
        lead_type: "meeting",
        source: getLeadSource("Home Page Contact"),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
      };
      if (form.quantity.trim()) payload.quantity = form.quantity.trim();
      if (messageParts.length) payload.message = messageParts.join(" · ");
      await submitLead(payload);
      setForm(INITIAL);
      navigate("/thank-you");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((Array.isArray(d) ? d[0]?.msg : d) || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <section className="section home-contact" id="contact" data-testid="home-contact-section">
      <div className="section-inner s10-grid">
        <div>
          <Reveal className="eyebrow cream">Get in Touch</Reveal>
          <Reveal as="h2" delay={1} className="section-headline cream">Let's find you the<br /><em>perfect gift.</em></Reveal>
          <form onSubmit={handleSubmit} className="s10-form" noValidate>
            <div className="s10-row">
              <input className="s10-input" placeholder="Your Name *" value={form.name} onChange={update("name")} data-testid="home-contact-name" required />
              <input className="s10-input" placeholder="Company Name *" value={form.company} onChange={update("company")} data-testid="home-contact-company" required />
            </div>
            <div className="s10-row">
              <input className="s10-input" type="tel" placeholder="Phone Number *" value={form.phone} onChange={update("phone")} data-testid="home-contact-phone" required />
              <input className="s10-input" type="email" placeholder="Email Address *" value={form.email} onChange={update("email")} data-testid="home-contact-email" required />
            </div>
            <div className="s10-row">
              <input className="s10-input" placeholder="Budget Range" value={form.budget} onChange={update("budget")} data-testid="home-contact-budget" />
              <input className="s10-input" placeholder="Quantity" value={form.quantity} onChange={update("quantity")} data-testid="home-contact-quantity" />
            </div>
            <textarea className="s10-input s10-textarea" placeholder="Your Question" rows={4} value={form.question} onChange={update("question")} data-testid="home-contact-question" />
            <div className="s10-cta-row">
              <button type="submit" className="btn-primary" disabled={submitting} data-testid="home-contact-submit">{submitting ? "Sending…" : "Send Message"}</button>
              <a className="btn-secondary" href={WHATSAPP_URL} target="_blank" rel="noreferrer noopener" data-testid="home-contact-whatsapp">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" /></svg>
                Chat on WhatsApp
              </a>
            </div>
          </form>
        </div>
        <div className="s10-info-panel">
          <div className="s10-info-eyebrow">What Happens Next?</div>
          <div className="s10-info-sub">Once you submit your enquiry, here's what you can expect.</div>
          <ul className="s10-info-list">
            <li>Response within <strong>3–4 business hours</strong></li>
            <li>Dedicated gifting specialist</li>
            <li>Product recommendations based on your budget</li>
            <li>Packaging &amp; branding consultation</li>
            <li>No-obligation quotation</li>
            <li>Pan-India delivery support</li>
          </ul>
          <div className="s10-info-help">
            <div className="s10-info-eyebrow-small">Need immediate assistance?</div>
            <div className="s10-info-help-text">Chat with us instantly on WhatsApp using the button in the bottom-right corner.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- INSIGHTS & RESOURCES ---------- */
function InsightsSection({ navigate }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchArticles({ limit: 3 })
      .then((list) => { if (alive) setItems(list || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="section home-insights" data-testid="home-insights-section">
      <div className="section-inner">
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Insights &amp; Resources</Reveal>
          <Reveal as="h2" delay={1} className="section-headline">
            Ideas that make <em>gifting thoughtful.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="section-sub" style={{ marginTop: 20, maxWidth: 720, marginLeft: "auto", marginRight: "auto" }}>
            Practical guides, gifting ideas and workplace insights to help HR, Procurement and People teams create thoughtful gifting experiences.
          </Reveal>
        </div>
        <div className="article-grid" data-testid="home-insights-grid">
          {items.map((a) => <ArticleCard key={a.id} article={a} />)}
        </div>
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <button type="button" className="hero-cta" onClick={() => navigate("/resources")} data-testid="home-insights-view-all">
            View All Articles
            <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden><path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </section>
  );
}

